import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import BreathingTimer from "@/components/wynn/BreathingTimer";
import GuidedSession from "@/app/screens/GuidedSession";
import { Wind, Sunrise, Moon, HeartPulse, ChevronLeft, Play, Lock } from "lucide-react";

const PRACTICES = [
  {
    id: "reset",
    title: "The 5-Minute Reset",
    desc: "A slow inhale–hold–exhale to settle the nervous system before you begin.",
    minutes: 5,
    icon: Wind,
    kind: "breathing",
  },
  {
    id: "opening",
    title: "Studio Opening",
    desc: "A short arrival ritual to soften the edge of starting.",
    minutes: 3,
    icon: Sunrise,
    kind: "guided",
    steps: [
      { at: 0, text: "Let your eyes soften. You don't have to make anything yet." },
      { at: 35, text: "Notice the weight of your hands. They already know how to begin." },
      { at: 75, text: "Bring to mind one small thing you'd like to touch today." },
      { at: 120, text: "Not the whole piece. Just the first gesture." },
      { at: 160, text: "When you're ready, carry this quiet into the work." },
    ],
  },
  {
    id: "evening",
    title: "Evening Wind-down",
    desc: "Lay down the day. A gentle close for tired makers.",
    minutes: 4,
    icon: Moon,
    kind: "guided",
    steps: [
      { at: 0, text: "The work is done for today. Let it rest where it is." },
      { at: 40, text: "Breathe out the parts that didn't go the way you hoped." },
      { at: 90, text: "Nothing was wasted. Even the unfinished is part of it." },
      { at: 140, text: "Let your shoulders drop. You carried enough." },
      { at: 200, text: "Tomorrow the thread will still be here, waiting kindly." },
    ],
  },
  {
    id: "body",
    title: "Body Release",
    desc: "For the body that holds your work. A short embodied reset.",
    minutes: 3,
    icon: HeartPulse,
    kind: "guided",
    steps: [
      { at: 0, text: "Unclench your jaw. Let your tongue rest soft in your mouth." },
      { at: 40, text: "Roll your shoulders back, slowly, twice." },
      { at: 80, text: "Soften the space between your eyebrows." },
      { at: 120, text: "Let your breath reach all the way to your belly." },
      { at: 160, text: "You can return to this whenever the work gets heavy." },
    ],
  },
];

export default function Practices() {
  const [active, setActive] = useState(null);
  const practice = PRACTICES.find((p) => p.id === active);

  if (practice) {
    return (
      <div data-testid="screen-practices" className="space-y-8">
        <button
          data-testid="practice-back"
          onClick={() => setActive(null)}
          className="inline-flex items-center gap-2 font-body text-[11px] uppercase tracking-[0.2em] text-[#F4F1EC]/55 hover:text-[#FFB7C6] transition-colors"
        >
          <ChevronLeft size={16} strokeWidth={1.6} />
          All practices
        </button>
        <div className="border border-[#F4F1EC]/12 bg-[#F4F1EC]/[0.03] p-8 md:p-12">
          {practice.kind === "breathing" ? (
            <BreathingTimer />
          ) : (
            <GuidedSession title={practice.title} minutes={practice.minutes} steps={practice.steps} />
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="screen-practices" className="space-y-8">
      <div>
        <div className="text-[11px] uppercase tracking-[0.32em] text-[#F4F1EC]/55 font-body">
          The library
        </div>
        <h1 className="mt-3 font-display tracking-tight leading-[1.02] text-[clamp(2rem,5vw,3rem)] font-medium">
          Practices to walk you <span className="italic font-light text-aurora">back</span>.
        </h1>
      </div>

      {/* Flagship teaser — locked, lives in the full app */}
      <button
        data-testid="practice-relief-locked"
        onClick={() =>
          toast("Coming in the full WYNN app", {
            description:
              "Instant-relief art therapy videos arrive with the full app. Join the waitlist from your Home screen.",
          })
        }
        className="relative w-full text-left border border-[#FFB7C6]/30 overflow-hidden group"
      >
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(120deg, rgba(255,183,198,0.22) 0%, rgba(230,191,255,0.18) 50%, rgba(168,230,255,0.16) 100%)",
          }}
        />
        <div aria-hidden className="absolute inset-0 wynn-grain opacity-30" />
        <div className="relative p-6 md:p-7 flex items-center gap-5">
          <span className="h-14 w-14 shrink-0 rounded-full bg-[#070608]/70 border border-[#F4F1EC]/30 inline-flex items-center justify-center transition-transform group-hover:scale-105">
            <Play size={22} strokeWidth={1.4} className="text-[#F4F1EC] ml-0.5" />
          </span>
          <div className="flex-1">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-[#FFB7C6] font-body">
              <Lock size={12} strokeWidth={1.8} /> Flagship · Full app
            </div>
            <div className="mt-1.5 font-display text-[24px] leading-tight text-[#F4F1EC]">
              Instant Relief — art therapy videos
            </div>
            <p className="mt-1 font-body text-sm text-[#F4F1EC]/65">
              Press-play tools for strong emotions — overwhelm, anger, grief — for instant
              emotional self-regulation.
            </p>
          </div>
        </div>
      </button>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {PRACTICES.map((p, i) => {
          const Icon = p.icon;
          return (
            <motion.button
              key={p.id}
              data-testid={`practice-${p.id}`}
              onClick={() => setActive(p.id)}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="group text-left border border-[#F4F1EC]/12 bg-[#F4F1EC]/[0.03] p-6 hover:border-[#FFB7C6]/40 transition-colors"
            >
              <div className="flex items-center justify-between">
                <Icon size={22} strokeWidth={1.4} className="text-[#FFB7C6] group-hover:text-[#A8E6FF] transition-colors" />
                <span className="font-body text-[11px] uppercase tracking-[0.2em] text-[#F4F1EC]/45">
                  {p.minutes} min
                </span>
              </div>
              <div className="mt-6 font-display text-[24px] leading-tight">{p.title}</div>
              <p className="mt-2 font-body text-sm leading-relaxed text-[#F4F1EC]/60">{p.desc}</p>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}
